import React from 'react'
import Game from './Game'
import { render, getConfig, CONTENT_TYPE, COLORS, initErrorMonitoring } from '@educaplay/core'
import { assetsUrl, GameProvider } from '@educaplay/core'
import { initialize } from '@educaplay/store'
import DictationConfigProvider from './components/DictationConfigProvider'
import { formatArrayText, groupMultimedia } from './utils/functions'
import CorrectionResults from './components/CorrectionResults'
import { getScore } from './utils/score'
import { getNumCorrectWords, getNumIncorrectWords } from './utils/correction'
import { Check } from '@educaplay/core/Icons/Check'
import { Wrong } from '@educaplay/core/Icons/Wrong'
import classes from './Main.module.scss'
import clsx from 'clsx'
import CorrectionEmptyResults from './components/CorrectionEmptyResults'
import { AudioComponentControl } from '@educaplay/core/components/AudioComponentControl'

initErrorMonitoring("https://549bbd780a52b6c2ae732bc4bc40902d@o4509038211104768.ingest.de.sentry.io/4509093762695248");

const gameImage = assetsUrl("DICTADO/logoActivity.png");
const linesURL = assetsUrl("DICTADO/lines.png");
const container = document.getElementById('root');
const POINTS = 100;
const PENALTY_DEFAULT = 0;


async function init() {
  const { data, texts, settings, user, title, description, author, id, url } = await getConfig().catch((err) =>
    console.error('Error al obtener la configuración: ', err)
  )
  const DICTADO_SOUNDS = {
    "drag": assetsUrl('DICTADO/arrastrar.mp3'),
    "goodActive": assetsUrl('DICTADO/bienactivo.mp3'),
    "lowWrite": assetsUrl('DICTADO/escribir_low.mp3'),
    "lowWrong": assetsUrl('DICTADO/mal_low.mp3'),
    "move": assetsUrl('DICTADO/mover.mp3'),
    "activeButton": assetsUrl('DICTADO/activar.mp3'),
    "place": assetsUrl('DICTADO/colocar.mp3'),
  }

  const { resources } = settings
  const filteredQuestions = groupMultimedia(data.BLOQUES
    .map((question, index) => ({ ...question, id: index }))
    .filter((question) => !(!question.MULTIMEDIA && !question.TEXTO)));

  const pointsScreen = POINTS / filteredQuestions.length;
  const allowChangeVelocity = data.CAMBIAR_VELOCIDAD === "si";
  const allowPause = !(data.EVITAR_PAUSAS === "si");
  const enableJumpLine = data.SENSIBLE_SALTOS === "si";
  const enableAccentuation = data.SENSIBLE_MAYUSCULAS_ACENTOS === "si";
  const penaltyValueNumber = Number(data.PENALIZACION);
  const enablePenalty = data.PENALIZACION_ACTIVA === "si" && penaltyValueNumber > 0;
  const penalty = enablePenalty ? penaltyValueNumber : false;
  const penaltySelected = enablePenalty ? penalty : PENALTY_DEFAULT;
  const questionTimeNumber = Number(data.TIEMPO);
  const questionTime = Number.isNaN(questionTimeNumber) ? 0 : questionTimeNumber;

  const questions = filteredQuestions.map(question => {
    const correctAnswer = formatArrayText(question.TEXTO, enableAccentuation);

    const checkAnswer = (answer) => {
      if (!answer) return false;
      const { corrections } = answer;
      const numIncorrectWords = getNumIncorrectWords(corrections);
      return numIncorrectWords === 0;
    }

    const calculatePoints = ({ corrections }) => {
      const numCorrectWords = getNumCorrectWords(corrections);
      if (numCorrectWords === 0) return 0;
      const screenPenalty = getScore(corrections, penaltySelected);
      return Math.max(pointsScreen - screenPenalty, 0);
    }

    const multimedia = question.MULTIMEDIA?.map(multimedia => {
      return `${resources}${multimedia}`;
    })

    return {
      bank: (question.ENUNCIADO || question.MULTIMEDIA) ? {
        text: question.ENUNCIADO,
        source: question.MULTIMEDIA_TIPO === CONTENT_TYPE.YOUTUBE ? question.MULTIMEDIA : multimedia,
        type: question.MULTIMEDIA_TIPO ?? CONTENT_TYPE.TEXT
      } : null,
      checkAnswer,
      correctAnswers: [correctAnswer],
      answer: null,
      time: questionTime,
      id: question.id,
      calculatePoints,
    }
  });

  initialize({
    lives: data.NUMERO_INTENTOS,
    liveActived: !!data.NUMERO_INTENTOS_INFINITOS && data.NUMERO_INTENTOS_INFINITOS !== "si",
    hasCountDown: data.MAXIMO === "si",
    countFooterSpace: true,
    randomized: data.ALEATORIO === "si",
    showSolutions: data.OCULTAR_RESPUESTAS !== "1",
    showRestart: data.OCULTAR_REINICIAR !== "1",
    showSocialMedia: data.OCULTAR_REDES !== "1",
    hasProgressBar: { view: true, onlyCorrect: false },
    finalText: data.TEXTO_FINAL,
    hasBank: true,
    semiCorrect: true,
    audioSettings: {
      showAudioControls: allowChangeVelocity,
      allowPause
    },
    questions,
    settings,
    title,
    description,
    author,
    id,
    url,
    user,
    customizations: {
      customLogo: data.LOGO,
      customBarLogo: data.FRANJA,
      backgroundImage: { src: data.BACKGROUND, showGame: true },
      colors: {
        primary: data.COLOR_FONDO,
        text: data.COLOR_FUENTE,
        action: data.COLOR_BOTONES,
        textAction: data.COLOR_FUENTE_B,
      }
    }
  });

  render(container, (
    // <React.StrictMode>
    <DictationConfigProvider
      enableJumpLine={enableJumpLine}
      enableAccentuation={enableAccentuation}
      penalty={penalty}
      backGroundNoteImage={linesURL}
    >
      <GameProvider
        sounds={DICTADO_SOUNDS}
        mappers={{
          report: (question) => {
            const isCorrect = question.checkAnswer(question.answer)
            return {
              s: isCorrect,
              i: question.id,
              a: question.answer.userAnswer ?? ""
            }
          }
        }}
        renders={{
          results: (questions) => {
            return (
              <div className={classes.answerdisposition}>
                {questions.map((question, index) => {
                  const hasBank = question.bank ? (question.bank.type === CONTENT_TYPE.AUDIO && question.bank.source) : false;
                  const isCorrect = question.checkAnswer(question.answer);
                  const correctAnswer = question.correctAnswers[0];
                  return (
                    <div key={index} className={classes.answerblock} style={{ backgroundColor: isCorrect ? "#559107" : "#E08876" }}>
                      <div className={classes.headeranswer}>
                        <div >
                          <span className={clsx(classes.number, isCorrect && classes.correct)} style={{ color: isCorrect ? "#FFFFFF" : "#112000" }}>{(index + 1) + "."}</span>
                        </div>
                        {hasBank ? <AudioComponentControl source={question.bank.source} color={COLORS.white} /> : <div />}
                        <div className={classes.symbol}>
                          {isCorrect ? <Check color={COLORS.green} /> : <Wrong color={COLORS.red} />}
                        </div>
                      </div>
                      <div className={clsx(classes.bodyanswer, isCorrect && classes.correct)} style={{ color: isCorrect ? "white" : "black" }}>
                        {question.answer ?
                          <CorrectionResults corrections={question.answer.corrections} />
                          :
                          <CorrectionEmptyResults correctAnswer={correctAnswer} />
                        }
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          }
        }}
        gameImage={gameImage}
        texts={texts}
        Game={Game}
      />
    </DictationConfigProvider>
    //</React.StrictMode> 
  ))
}

init();